'use client';

import { useState, useMemo, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import { FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Buscador from './Buscador';
import Map from './Map';
import PointCard from './PointCard';
import { filterPointsByService } from './filterPointsByService';
import BuscadorButton from '@/components/Botones/Buscador';
import { useLocale } from '@/context/LocaleContext';

/**
 * Haversine distance in km.
 */
const distanceKm = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const FILTER_QUERY_PARAMS = new Set([
  'focus', 'mode', 'set', 'segmentBy', 'segmentValue', 'q', 'searchBy', 'limit',
  'center', 'centerLat', 'centerLng', 'zoom'
]);
const POINTS_BATCH_SIZE = 10;

/**
 * Página /conoce-los-puntos-hop: mapa + lista de puntos HOP con reseller.
 * Layout grid responsive: mapa a la izquierda, lista a la derecha.
 *
 * @param {{ pickup: object[], dropOff: object[] }} points
 * @param {Object} dict
 * @param {boolean} hasResellerData
 */
const PuntosParaEnvios = ({
  points,
  dict,
  countryMapCenter,
  countryMapZoom = 4,
  hasPointsData,
  fitPointsToBounds = true,
  initialServiceFilter = 'all',
  debugSellerBadge = false,
  hideNavBar = false
}) => {
  const router = useRouter();
  const locale = useLocale();
  const copy = dict?.shipping_points || {};
  const routeFocus = String(router.query.focus || '').toLowerCase();
  const pointSearchQuery = typeof router.query.q === 'string' ? router.query.q : '';
  const focusedServiceFilter = ['pickup', 'dropoff', 'buy', 'sell'].includes(routeFocus)
    ? initialServiceFilter
    : null;
  const [address, setAddress] = useState(null);
  const [userPosition, setUserPosition] = useState(null);
  const [lastUserPosition, setLastUserPosition] = useState(null);
  const [searchMethod, setSearchMethod] = useState('direccion');
  const [serviceFilter, setServiceFilter] = useState(initialServiceFilter);
  const [visiblePointsLimit, setVisiblePointsLimit] = useState(POINTS_BATCH_SIZE);
  const [resetVersion, setResetVersion] = useState(0);
  const [resetView, setResetView] = useState(null);
  const [isResetting, setIsResetting] = useState(false);
  const pointListRef = useRef(null);
  const hasActiveFilters =
    serviceFilter !== 'all' ||
    Boolean(address || userPosition) ||
    [...FILTER_QUERY_PARAMS].some((key) => router.query[key] !== undefined);

  useEffect(() => {
    const list = pointListRef.current;
    if (!list) return undefined;

    const handleWheel = (event) => {
      if (!list.contains(event.target)) return;

      const maxScrollTop = list.scrollHeight - list.clientHeight;
      if (maxScrollTop <= 0) return;

      const previousScrollTop = list.scrollTop;
      list.scrollTop = Math.max(0, Math.min(maxScrollTop, previousScrollTop + event.deltaY));
      if (list.scrollTop !== previousScrollTop) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };

    document.addEventListener('wheel', handleWheel, { capture: true, passive: false });
    return () => document.removeEventListener('wheel', handleWheel, { capture: true });
  }, [hasPointsData]);

  const visiblePoints = useMemo(
    () => filterPointsByService(points, serviceFilter),
    [points, serviceFilter]
  );

  const allPoints = useMemo(() => {
    const pickup = visiblePoints.pickup;
    const dropOff = visiblePoints.dropOff;
    const seen = new Set();
    const merged = [];
    for (const p of [...pickup, ...dropOff]) {
      const key = p.id || JSON.stringify(p);
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(p);
      }
    }
    return merged;
  }, [visiblePoints]);

  const sortedPoints = useMemo(() => {
    const center = userPosition || address;
    if (!center) return allPoints;

    return [...allPoints]
      .map((p) => ({
        ...p,
        _distance: distanceKm(center.lat, center.lng, parseFloat(p.lat), parseFloat(p.lng))
      }))
      .sort((a, b) => a._distance - b._distance);
  }, [allPoints, userPosition, address]);

  const hasLocationFilter = Boolean(userPosition || address);
  const pointsToDisplay = sortedPoints.slice(0, visiblePointsLimit);
  const firstDisplayedPoint = pointsToDisplay[0];
  const mapFocusPoint = useMemo(() => {
    if (!pointSearchQuery || router.query.searchBy !== 'point' || !firstDisplayedPoint) return null;

    const lat = parseFloat(firstDisplayedPoint.lat);
    const lng = parseFloat(firstDisplayedPoint.lng);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
  }, [
    pointSearchQuery,
    router.query.searchBy,
    firstDisplayedPoint
  ]);

  const handleServiceFilterChange = (filter) => {
    setServiceFilter(filter);
    setVisiblePointsLimit(POINTS_BATCH_SIZE);
  };

  const handlePointSearch = (query) => {
    setVisiblePointsLimit(POINTS_BATCH_SIZE);
    router.push(
      { pathname: router.pathname, query: { ...router.query, q: query, searchBy: 'point' } },
      undefined,
      { scroll: false, locale }
    );
  };

  const clearPointSearchQuery = () => {
    if (router.query.q === undefined && router.query.searchBy === undefined) return;

    const remainingQuery = Object.fromEntries(
      Object.entries(router.query).filter(([key]) => key !== 'q' && key !== 'searchBy')
    );
    router.replace({ pathname: router.pathname, query: remainingQuery }, undefined, {
      scroll: false,
      locale
    });
  };

  const handleClearSearch = () => {
    setAddress(null);
    setUserPosition(null);
    setVisiblePointsLimit(POINTS_BATCH_SIZE);
    setResetVersion((version) => version + 1);
    clearPointSearchQuery();
  };

  const handleShowAllPoints = () => {
    handleServiceFilterChange('all');
    if (router.query.focus === undefined) return;

    const remainingQuery = Object.fromEntries(
      Object.entries(router.query).filter(([key]) => key !== 'focus')
    );
    router.replace({ pathname: router.pathname, query: remainingQuery }, undefined, {
      shallow: true,
      scroll: false,
      locale
    });
  };

  const handleAddressSelect = ({ lat, lng }) => {
    clearPointSearchQuery();
    setAddress({ lat, lng });
    setUserPosition(null);
    setVisiblePointsLimit(POINTS_BATCH_SIZE);
    setResetView(null);
    setSearchMethod('direccion');
  };

  const handleGeoLocate = ({ lat, lng }) => {
    clearPointSearchQuery();
    const position = { lat, lng };
    setUserPosition(position);
    setLastUserPosition(position);
    setAddress(null);
    setVisiblePointsLimit(POINTS_BATCH_SIZE);
    setResetView(null);
    setSearchMethod('geolocalizacion');
  };

  const handleResetFilters = async () => {
    handleServiceFilterChange('all');
    setAddress(null);
    setUserPosition(null);
    setSearchMethod('direccion');
    setResetVersion((version) => version + 1);
    setIsResetting(true);

    let sharedPosition = null;
    let locationPermissionDenied = false;
    try {
      if (navigator.geolocation && navigator.permissions?.query) {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        locationPermissionDenied = permission.state === 'denied';
        if (permission.state === 'granted') {
          sharedPosition = await new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
              ({ coords }) => resolve({ lat: coords.latitude, lng: coords.longitude }),
              () => resolve(null),
              { maximumAge: 300000, timeout: 5000 }
            );
          });
        }
      }
    } catch {
      sharedPosition = null;
    }

    sharedPosition = sharedPosition || (locationPermissionDenied ? null : lastUserPosition);
    setResetView({
      center: sharedPosition || countryMapCenter || { lat: dict.map.lat, lng: dict.map.lng },
      zoom: sharedPosition ? 14 : countryMapZoom,
      useLocation: Boolean(sharedPosition)
    });
    setIsResetting(false);

    const remainingQuery = Object.fromEntries(
      Object.entries(router.query).filter(([key]) => !FILTER_QUERY_PARAMS.has(key))
    );
    if (Object.keys(remainingQuery).length !== Object.keys(router.query).length) {
      router.replace({ pathname: router.pathname, query: remainingQuery }, undefined, { scroll: false, locale });
    }
  };

  return (
    <section className="w-full">
      {/* Hero */}
      <div
        className={`w-full rounded-[3rem] sm:rounded-[5.2rem] bg-cyan-light ${
          hideNavBar ? 'pt-[40px]' : 'pt-[100px]'
        } pb-30 px-0`}
      >
        <div className="site-max mx-auto px-20">
          <h1 className="text-cyan-dark text-24 sm:text-32 lg:text-40 font-bold leading-tight max-w-[60rem]">
            {copy.title}
          </h1>
          <p className="text-cyan-dark text-14 sm:text-16 opacity-70 mt-10 max-w-[50rem]">
            {copy.subtitle}
          </p>

          <div className="mt-20 max-w-[60rem] lg:hidden">
            <Buscador
              onAddressSelect={handleAddressSelect}
              onGeoLocate={handleGeoLocate}
              onPointSearch={handlePointSearch}
              onClearPointSearch={clearPointSearchQuery}
              initialPointSearchQuery={pointSearchQuery}
              dict={dict}
              inputId="puntos-envios-mobile"
              resetVersion={resetVersion}
            />
          </div>
        </div>
      </div>

      {/* Contenido: mapa + lista */}
      {!hasPointsData ? (
        <div className="site-max mx-auto px-20 py-40 text-center">
          <svg
            className="mx-auto mb-16"
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              fill="#064246"
              opacity="0.3"
              d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z"
            />
          </svg>
          <p className="text-cyan-dark text-16 sm:text-18 font-semibold mb-8">
            No hay puntos disponibles en este momento
          </p>
          <p className="text-cyan-dark text-14 opacity-60">
            {copy.empty_hint}
          </p>
        </div>
      ) : (
        <div className="site-max mx-auto px-0 lg:px-20 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.6fr)_minmax(320px,1fr)] gap-0 lg:gap-20">
            {/* Mapa */}
            <div className="relative w-full h-[50vh] lg:h-[70vh] order-1 bg-cyan-light rounded-0 lg:rounded-[.8rem] overflow-hidden">
              <Map
                puntos={visiblePoints}
                address={address}
                userPosition={userPosition}
                searchMethod={searchMethod}
                dict={dict}
                resetView={resetView}
                focusPoint={mapFocusPoint}
                fitPointsToBounds={fitPointsToBounds}
                debugSellerBadge={debugSellerBadge}
              />
              <div className="absolute z-10 bottom-28 left-10 right-10">
                <div
                  role="group"
                  aria-label="Filtrar puntos por servicio"
                  className="flex gap-6 overflow-x-auto pb-2 pr-2"
                >
                  <div className="shrink-0">
                    <BuscadorButton
                      ariaLabel="Todos los puntos"
                      compact
                      label="Todos"
                      active={serviceFilter === 'all'}
                      onClick={handleShowAllPoints}
                    />
                  </div>
                  {(focusedServiceFilter === null || focusedServiceFilter === 'pickup') && (
                    <div className="shrink-0">
                      <BuscadorButton
                        ariaLabel={copy.pickup_filter_aria}
                        compact
                        label={copy.pickup_filter_label}
                        active={serviceFilter === 'pickup'}
                        onClick={() => handleServiceFilterChange('pickup')}
                      />
                    </div>
                  )}
                  {(focusedServiceFilter === null || focusedServiceFilter === 'dropoff') && (
                    <div className="shrink-0">
                      <BuscadorButton
                        ariaLabel="Despachar envío"
                        compact
                        label="Despacho"
                        active={serviceFilter === 'dropoff'}
                        onClick={() => handleServiceFilterChange('dropoff')}
                      />
                    </div>
                  )}
                  {(focusedServiceFilter === null || focusedServiceFilter === 'buy') && (
                    <div className="shrink-0">
                      <BuscadorButton
                        ariaLabel="Venta de envíos"
                        compact
                        label="Venta de envíos"
                        active={serviceFilter === 'buy'}
                        onClick={() => handleServiceFilterChange('buy')}
                      />
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={handleResetFilters}
                disabled={isResetting}
                aria-label="Borrar filtros y mostrar todos los puntos"
                title="Borrar filtros y mostrar todos los puntos"
                aria-pressed={hasActiveFilters}
                className="absolute left-16 top-16 z-10 grid h-40 w-40 place-items-center rounded-full border border-cyan-dark/30 bg-white text-cyan-dark shadow-md transition-colors hover:bg-cyan-dark hover:text-white aria-pressed:border-cyan-dark aria-pressed:bg-cyan-dark aria-pressed:text-white disabled:opacity-50"
              >
                <FunnelIcon aria-hidden="true" className="h-20 w-20" />
                <span className="absolute -bottom-2 -right-2 grid h-18 w-18 place-items-center rounded-full border border-white bg-red text-white">
                  <XMarkIcon aria-hidden="true" className="h-12 w-12" />
                </span>
              </button>
            </div>

            {/* Lista de puntos */}
            <div
              ref={pointListRef}
              className="order-2 px-20 lg:px-0 py-20 lg:py-0 max-h-[50vh] lg:max-h-[70vh] overflow-y-auto overscroll-y-contain"
            >
              <div className="lg:sticky lg:top-0 lg:z-10 lg:bg-white">
                <div className="hidden lg:block mb-16">
                  <Buscador
                    onAddressSelect={handleAddressSelect}
                    onGeoLocate={handleGeoLocate}
                    onPointSearch={handlePointSearch}
                    onClearPointSearch={clearPointSearchQuery}
                    initialPointSearchQuery={pointSearchQuery}
                    dict={dict}
                    inputId="puntos-envios-desktop"
                    resetVersion={resetVersion}
                  />
                </div>
                <div className="flex items-center justify-between mb-16">
                  <p className="text-14 sm:text-16 font-semibold text-cyan-dark">
                    {hasLocationFilter
                      ? `Mostrando ${pointsToDisplay.length} de ${sortedPoints.length} puntos, ordenados por cercanía`
                      : `Mostrando ${pointsToDisplay.length} de ${sortedPoints.length} punto${sortedPoints.length !== 1 ? 's' : ''} encontrado${sortedPoints.length !== 1 ? 's' : ''}`}
                  </p>
                  {(userPosition || address || pointSearchQuery) && (
                    <button
                      onClick={handleClearSearch}
                      className="text-12 text-red hover:underline"
                    >
                      Limpiar búsqueda
                    </button>
                  )}
                </div>
              </div>

              {pointsToDisplay.length === 0 ? (
                <div className="text-center py-30">
                  <p className="text-cyan-dark text-14 opacity-60">
                    No se encontraron puntos cerca de esta ubicación.
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-12">
                  {pointsToDisplay.map((punto) => (
                    <PointCard
                      key={punto.id}
                      punto={punto}
                      searchMethod={searchMethod}
                      distance={punto._distance}
                    />
                  ))}
                </div>
              )}
              {pointsToDisplay.length < sortedPoints.length && (
                <button
                  type="button"
                  onClick={() =>
                    setVisiblePointsLimit((limit) =>
                      Math.min(limit + POINTS_BATCH_SIZE, sortedPoints.length)
                    )
                  }
                  className="mt-16 w-full rounded-full border border-cyan-dark px-16 py-10 text-14 font-semibold text-cyan-dark transition-colors hover:bg-cyan-dark hover:text-white"
                >
                  Mostrar {Math.min(POINTS_BATCH_SIZE, sortedPoints.length - pointsToDisplay.length)} más
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PuntosParaEnvios;